import React, {forwardRef} from "react";
import {styled, Typography} from "@mui/material";
import {KahunColorMap} from "fe-common/ui/assets/colorMap";
import {CHAT_ELEMENT_HEIGHT} from "../consts/sizeConsts";
import {useNavigation} from "../../state/navigationContext";
import {ReactComponent as QuestionMarkIcon} from "../../assets/featureDescriptionIcon.svg";
import {ShowInfoIcon} from "./questionDescription";
import clsx from "clsx";
import {onRtl} from "fe-web-components/rtl/rtlStyles";
import {PatientColorMap} from "../../layout/patientColorMap";
import {PatientThemeImage} from "../../themes/PatientThemeImage";

//region [[ Styles ]]

const QuestionView = styled("div")(({ icon }: { icon: "true" | "false" }) => ({
  backgroundColor: KahunColorMap.LightGrayBackground,
  display: "flex",
  flexDirection: "column",
  width: "fit-content",
  padding: "0px",
  borderRadius: "30px",
  // Keep very specific rule for color so it would be harder to override
  "& p.question-text": {
    color: KahunColorMap.BlackPatientFacing,
    // Override helix generic ".entry-content p" rule
    marginBottom: 0,
  },
  flex: 2, // Let it grow over other elements (ie: description icon button)
  "&.mini-view": {
    background: "transparent",
    borderRadius: "none",
    "& .question-text": {
      padding: "4px",
      fontWeight: 500,
      lineHeight: "18px",
      fontSize: "12px",
    },
  },
}));

const DoctorImageContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "35px",
  height: "35px",
  marginLeft: "5px",
  ...onRtl({
    marginRight: "5px",
  }),
  "& img": {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
  },
  padding: "0px",
});

const TopPart = styled("div")({
  display: "flex",
  minHeight: CHAT_ELEMENT_HEIGHT,
  justifyContent: "space-between",
  alignItems: "center",
  width: "fit-content",
});

const LeftPart = styled("div")({
  display: "flex",
  width: "fit-content",
  alignItems: "center",
});

const RightPart = styled("div")({});

const BottomPart = styled("div")({
  padding: "10px",
  display: "flex",
  justifyContent: "center",
});

const QuestionText = styled(Typography)({
  whiteSpace: "break-spaces",
  alignSelf: "center",
  padding: "8px",
  fontWeight: 500,
  lineHeight: "22px",
  fontSize: "16px",
  color: KahunColorMap.BlackPatientFacing,
  marginRight: "14px",
  marginLeft: "0px",
  ...onRtl({
    marginLeft: "14px",
    marginRight: "0px",
  }),
});

const MiniViewQuestionText = styled("div")({
  fontSize: "15px",
  fontWeight: 700,
  marginBottom: "16px",
  color: PatientColorMap.Default,
});
//endregion [[ Styles ]]

//region [[ Props ]]
export interface DocQuestionProps {
  className?: string;
  text: string;
  onClick?: () => void;
  icon?: any;
  onShowDescription?: () => void;
  // Mini view of quesions is used in forms, where we display a minimal ui to answer the quesion
  miniView?: boolean;
  children?: React.ReactNode;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const DocQuestion = forwardRef<HTMLDivElement, DocQuestionProps>(
  ({ text, ...props }, ref) => {
    const { themeSettings } = useNavigation();

    // An easy hack to hide questions in forms
    if (props.miniView) {
      return <MiniViewQuestionText>{text}</MiniViewQuestionText>;
    }

    return (
      <QuestionView
        className={clsx(
          props.className,
          "doc-question",
          props.miniView ? "mini-view" : "",
        )}
        onClick={props.onClick}
        icon={props.icon ? "true" : "false"}
        ref={ref}
      >
        <TopPart className={"top-part"}>
          <LeftPart className={"left-part"}>
            {!props.miniView && (
              <DoctorImageContainer>
                {themeSettings && (
                  <PatientThemeImage source={themeSettings.botIcon} />
                )}
              </DoctorImageContainer>
            )}
            <QuestionText className={"question-text"}>{text}</QuestionText>
          </LeftPart>
          {props.onShowDescription && (
            <RightPart>
              <ShowInfoIcon>
                <QuestionMarkIcon onClick={props.onShowDescription} />
              </ShowInfoIcon>
            </RightPart>
          )}
        </TopPart>
        {props.icon && <BottomPart>{props.icon}</BottomPart>}
        {props.children}
      </QuestionView>
    );
  },
);
